import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Button, Form, Alert, Card, Table } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { isSuperAdmin, isOperationsAdmin } from '../../utils/roleBasedAccess';
import '../../pages/ManageTournaments/ManageTournaments.css';

// Dummy data for teams and venues
const DUMMY_TEAMS = {
  '1': [
    { id: 't1', name: 'Mumbai United', abbreviation: 'MU' },
    { id: 't2', name: 'Pune Warriors', abbreviation: 'PW' },
    { id: 't3', name: 'Delhi Dynamos', abbreviation: 'DD' },
    { id: 't4', name: 'Chennai Super Kings', abbreviation: 'CSK' },
    { id: 't5', name: 'Bengaluru FC', abbreviation: 'BFC' }
  ],
  '2': [
    { id: 't1', name: 'Pune Tennis Club', abbreviation: 'PTC' },
    { id: 't2', name: 'Mumbai Tennis Academy', abbreviation: 'MTA' },
    { id: 't3', name: 'Delhi Tennis Center', abbreviation: 'DTC' },
    { id: 't4', name: 'Chennai Tennis Club', abbreviation: 'CTC' }
  ]
};

const DUMMY_VENUES = [
  { id: 'v1', name: 'Mumbai Stadium' },
  { id: 'v2', name: 'Pune Sports Complex' },
  { id: 'v3', name: 'Delhi Football Ground' },
  { id: 'v4', name: 'Chennai Sports Center' },
  { id: 'v5', name: 'Bengaluru Sports Hub' },
  { id: 'v6', name: 'Pune Tennis Center' },
  { id: 'v7', name: 'Delhi Tennis Center' }
];

const MATCH_TYPES = [
  { value: 'group_stage', label: 'Group Stage' },
  { value: 'knockout', label: 'Knockout' },
  { value: 'final', label: 'Final' },
  { value: 'semi_final', label: 'Semi Final' },
  { value: 'quarter_final', label: 'Quarter Final' }
];

// Dummy data for group fixtures
const DUMMY_GROUP_FIXTURES = {
  '1': {
    'g1': [
      { id: 'fx1', homeTeam: 'Mumbai United', awayTeam: 'Pune Warriors', date: '2025-01-15', time: '10:00 AM', venue: 'Mumbai Stadium', matchType: 'group_stage' },
      { id: 'fx2', homeTeam: 'Mumbai United', awayTeam: 'Bengaluru FC', date: '2025-01-18', time: '2:00 PM', venue: 'Mumbai Stadium', matchType: 'group_stage' },
      { id: 'fx3', homeTeam: 'Pune Warriors', awayTeam: 'Bengaluru FC', date: '2025-01-20', time: '4:00 PM', venue: 'Pune Sports Complex', matchType: 'group_stage' }
    ],
    'g2': [
      { id: 'fx4', homeTeam: 'Delhi Dynamos', awayTeam: 'Chennai Super Kings', date: '2025-01-16', time: '2:00 PM', venue: 'Delhi Stadium', matchType: 'group_stage' }
    ],
    'g3': [
      { id: 'fx5', homeTeam: 'Bengaluru FC', awayTeam: 'Mumbai United', date: '2025-01-17', time: '4:00 PM', venue: 'Bengaluru Stadium', matchType: 'group_stage' }
    ]
  },
  '2': {
    'g1': [
      { id: 'fx1', homeTeam: 'Pune Tennis Club', awayTeam: 'Mumbai Tennis Academy', date: '2025-01-20', time: '9:00 AM', venue: 'Pune Tennis Center', matchType: 'group_stage' },
      { id: 'fx2', homeTeam: 'Pune Tennis Club', awayTeam: 'Delhi Tennis Center', date: '2025-01-22', time: '11:00 AM', venue: 'Pune Tennis Center', matchType: 'group_stage' }
    ],
    'g2': [
      { id: 'fx3', homeTeam: 'Delhi Tennis Center', awayTeam: 'Chennai Tennis Club', date: '2025-01-21', time: '11:00 AM', venue: 'Delhi Tennis Center', matchType: 'group_stage' }
    ]
  }
};

const DUMMY_GROUP_DETAILS = {
  '1': {
    'g1': { id: 'g1', name: 'Group A', fixtureId: '1' },
    'g2': { id: 'g2', name: 'Group B', fixtureId: '1' },
    'g3': { id: 'g3', name: 'Group C', fixtureId: '1' }
  },
  '2': {
    'g1': { id: 'g1', name: 'Group A', fixtureId: '2' },
    'g2': { id: 'g2', name: 'Group B', fixtureId: '2' }
  }
};

const DUMMY_FIXTURE_DETAILS = {
  '1': { tournamentName: 'SFL Football League Mumbai Edition' },
  '2': { tournamentName: 'SFL Tennis Championship Pune' }
};

interface FixtureForm {
  id: string;
  homeTeam: { value: string; label: string } | null;
  awayTeam: { value: string; label: string } | null;
  date: string;
  time: string;
  venue: { value: string; label: string } | null;
  matchType: { value: string; label: string } | null;
}

const GroupFixturesEditPage: React.FC = () => {
  const { fixtureId, groupId } = useParams<{ fixtureId: string; groupId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // Check if we're in view mode
  const isViewMode = location.state?.viewMode || false;

  const [groupData, setGroupData] = useState<any>(null);
  const [fixtures, setFixtures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form data for all fixtures
  const [fixtureForms, setFixtureForms] = useState<FixtureForm[]>([]);

  // Check if user has access
  const userData = { ...user, subRole: role, permissions: permissions };
  const isSuperAdminUser = isSuperAdmin(userData);
  const isOperationsAdminUser = isOperationsAdmin(userData);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Manage Tournaments', onClick: () => navigate('/fixtures') },
    { label: 'Fixture Details', onClick: () => navigate(`/fixtures/${fixtureId}`, { 
      state: { 
        activeTab: 'fixtures',
        selectedTournament: location.state?.selectedTournament,
        selectedFixture: location.state?.selectedFixture
      } 
    }) },
    { label: `${groupData?.name} Fixtures`, active: true }
  ];

  useEffect(() => {
    if (!isSuperAdminUser && !isOperationsAdminUser) {
      setLoading(false);
      return;
    }

    // Load group fixtures data
    const loadGroupFixturesData = () => {
      try {
        setLoading(true);
        setError(null);
        setTimeout(() => {
          const groupDetails = DUMMY_GROUP_DETAILS[fixtureId as keyof typeof DUMMY_GROUP_DETAILS];
          const group = groupDetails?.[groupId as keyof typeof groupDetails];
          
          if (group) {
            setGroupData(group);
            
            const fixtureDetails = DUMMY_FIXTURE_DETAILS[fixtureId as keyof typeof DUMMY_FIXTURE_DETAILS];
            if (fixtureDetails) {
              setGroupData((prev: any) => ({ ...prev, tournamentName: fixtureDetails.tournamentName }));
            }

            const groupFixtures = DUMMY_GROUP_FIXTURES[fixtureId as keyof typeof DUMMY_GROUP_FIXTURES];
            const fixtures = groupFixtures?.[groupId as keyof typeof groupFixtures] || [];
            setFixtures(fixtures);

            // Initialize form data
            const teams = DUMMY_TEAMS[fixtureId as keyof typeof DUMMY_TEAMS] || [];
            const teamOptions = teams.map(team => ({ value: team.id, label: team.name }));
            const venueOptions = DUMMY_VENUES.map(venue => ({ value: venue.id, label: venue.name }));

            const forms = fixtures.map((fixture: any) => {
              const homeTeam = teams.find(team => team.name === fixture.homeTeam);
              const awayTeam = teams.find(team => team.name === fixture.awayTeam);
              const venue = DUMMY_VENUES.find(v => v.name === fixture.venue);
              const matchType = MATCH_TYPES.find(mt => mt.value === fixture.matchType);

              return {
                id: fixture.id,
                homeTeam: homeTeam ? { value: homeTeam.id, label: homeTeam.name } : null,
                awayTeam: awayTeam ? { value: awayTeam.id, label: awayTeam.name } : null,
                date: fixture.date,
                time: fixture.time,
                venue: venue ? { value: venue.id, label: venue.name } : null,
                matchType: matchType || null,
              };
            });

            setFixtureForms(forms);
          } else {
            setError('Group not found');
          }
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error loading group fixtures data:', error);
        setError('Failed to load group fixtures data');
        setLoading(false);
      }
    };

    loadGroupFixturesData();
  }, [fixtureId, groupId, isSuperAdminUser, isOperationsAdminUser]);

  const handleBack = () => {
    navigate(`/fixtures/${fixtureId}`, { 
      state: { 
        activeTab: 'fixtures',
        selectedTournament: location.state?.selectedTournament,
        selectedFixture: location.state?.selectedFixture
      } 
    });
  };

  const handleFixtureChange = (fixtureIndex: number, field: keyof FixtureForm, value: any) => {
    setFixtureForms(prev => prev.map((form, index) => 
      index === fixtureIndex ? { ...form, [field]: value } : form
    ));
  };

  const handleSave = async () => {
    // Validate all forms
    const hasErrors = fixtureForms.some(form => 
      !form.homeTeam || !form.awayTeam || !form.date || !form.time || !form.venue || !form.matchType
    );

    if (hasErrors) {
      setError('Please fill all required fields for all fixtures.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update the fixtures data
      const updatedFixtures = fixtureForms.map(form => {
        const teams = DUMMY_TEAMS[fixtureId as keyof typeof DUMMY_TEAMS] || [];
        const homeTeam = teams.find(team => team.id === form.homeTeam?.value);
        const awayTeam = teams.find(team => team.id === form.awayTeam?.value);
        const venue = DUMMY_VENUES.find(v => v.id === form.venue?.value);

        return {
          id: form.id,
          homeTeam: homeTeam?.name || '',
          awayTeam: awayTeam?.name || '',
          date: form.date,
          time: form.time,
          venue: venue?.name || '',
          matchType: form.matchType?.value || '',
        };
      });

      setFixtures(updatedFixtures);
      setSuccess('Group fixtures updated successfully!');
    } catch (err) {
      setError('Failed to update group fixtures.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddFixture = () => {
    const newFixture: FixtureForm = {
      id: `fx${Date.now()}`,
      homeTeam: null,
      awayTeam: null,
      date: '',
      time: '',
      venue: null,
      matchType: null,
    };
    setFixtureForms(prev => [...prev, newFixture]);
  };

  const handleRemoveFixture = (fixtureIndex: number) => {
    setFixtureForms(prev => prev.filter((_, index) => index !== fixtureIndex));
  };

  if (!isSuperAdminUser && !isOperationsAdminUser) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={breadcrumbItems} />
          </div>

          {/* Header Shimmer */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '24px',
            padding: '0 15px'
          }}>
            <div className="shimmer-image" style={{ width: '40px', height: '40px', borderRadius: '8px' }}></div>
            <div className="shimmer-image" style={{ width: '60px', height: '60px', borderRadius: '12px' }}></div>
            <div style={{ flex: 1 }}>
              <div className="shimmer-text" style={{ width: '300px', height: '24px', marginBottom: '8px' }}></div>
              <div className="shimmer-badge" style={{ width: '200px', height: '28px', borderRadius: '12px' }}></div>
            </div>
            <div className="shimmer-button" style={{ width: '120px', height: '40px', borderRadius: '8px' }}></div>
          </div>

          {/* Card Shimmer */}
          <Row>
            <Col>
              <Card className="shadow-sm">
                <Card.Header className="bg-white border-bottom">
                  <div className="shimmer-text" style={{ width: '200px', height: '20px' }}></div>
                </Card.Header>
                <Card.Body>
                  <div className="tournament-table-wrapper">
                    <ShimmerLoader type="table" rows={3} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error && !groupData) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3 className="text-danger">Error</h3>
            <p>{error}</p>
            <Button variant="primary" onClick={handleBack}>
              Back to Fixture Details
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const teams = DUMMY_TEAMS[fixtureId as keyof typeof DUMMY_TEAMS] || [];
  const teamOptions = teams.map(team => ({ value: team.id, label: team.name }));
  const venueOptions = DUMMY_VENUES.map(venue => ({ value: venue.id, label: venue.name }));

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {/* Group Fixtures Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
          padding: '0 15px'
        }}>
          <button
            className="back-button"
            onClick={handleBack}
            style={{ position: 'relative', zIndex: 10 }}
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #e9ecef'
          }}>
            <i className="ri-group-line" style={{ fontSize: '24px', color: '#6c757d' }}></i>
          </div>
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#212529',
              margin: 0,
              lineHeight: '1.2'
            }}>
              {isViewMode ? 'View' : 'Edit'} {groupData?.name} Fixtures
            </h1>
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              padding: '6px 10px',
              borderRadius: '12px',
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              border: '1px solid #bbdefb',
              minWidth: '68px',
              height: '28px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              lineHeight: '1',
              margin: '0'
            }}>
              {groupData?.tournamentName}
            </span>
          </div>
          <div>
            {/* Add Fixture button moved to bottom for better UX */}
          </div>
        </div>

        <Row>
          <Col>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h4 className="mb-0">{isViewMode ? 'View Group Fixtures' : 'Edit Group Fixtures'}</h4>
              </Card.Header>
              <Card.Body>
                {fixtureForms.length > 0 ? (
                  <div className="table-responsive">
                    <Table responsive className="table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Home Team</th>
                          <th>Away Team</th>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Venue</th>
                          <th>Match Type</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fixtureForms.map((form, index) => (
                          <tr key={form.id}>
                            <td style={{ position: 'relative', zIndex: 1 }}>
                              <Select
                                value={form.homeTeam}
                                onChange={(selectedOption) => handleFixtureChange(index, 'homeTeam', selectedOption)}
                                options={teamOptions}
                                placeholder="Select home team"
                                isClearable
                                isDisabled={isViewMode}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </td>
                            <td style={{ position: 'relative', zIndex: 1 }}>
                              <Select
                                value={form.awayTeam}
                                onChange={(selectedOption) => handleFixtureChange(index, 'awayTeam', selectedOption)}
                                options={teamOptions}
                                placeholder="Select away team"
                                isClearable
                                isDisabled={isViewMode}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="date"
                                value={form.date}
                                onChange={(e) => handleFixtureChange(index, 'date', e.target.value)}
                                disabled={isViewMode}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="time"
                                value={form.time}
                                onChange={(e) => handleFixtureChange(index, 'time', e.target.value)}
                                disabled={isViewMode}
                              />
                            </td>
                            <td style={{ position: 'relative', zIndex: 1 }}>
                              <Select
                                value={form.venue}
                                onChange={(selectedOption) => handleFixtureChange(index, 'venue', selectedOption)}
                                options={venueOptions}
                                placeholder="Select venue"
                                isClearable
                                isDisabled={isViewMode}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </td>
                            <td style={{ position: 'relative', zIndex: 1 }}>
                              <Select
                                value={form.matchType}
                                onChange={(selectedOption) => handleFixtureChange(index, 'matchType', selectedOption)}
                                options={MATCH_TYPES}
                                placeholder="Select match type"
                                isClearable
                                isDisabled={isViewMode}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </td>
                            <td>
                              <div className="tournament-actions">
                                <button
                                  className="action-btn btn-delete"
                                  onClick={() => handleRemoveFixture(index)}
                                  title="Remove Fixture"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No fixtures in this group yet.</p>
                    {!isViewMode && (
                      <Button variant="primary" onClick={handleAddFixture}>
                        Add First Fixture
                      </Button>
                    )}
                    {isViewMode && (
                      <Button variant="outline-secondary" onClick={handleBack}>
                        Back
                      </Button>
                    )}
                  </div>
                )}

                {fixtureForms.length > 0 && !isViewMode && (
                  <div className="d-flex gap-3 justify-content-between mt-4">
                    <Button
                      variant="outline-primary"
                      onClick={handleAddFixture}
                      disabled={saving}
                    >
                      + Add Fixture
                    </Button>
                    <div className="d-flex gap-3">
                      <Button
                        variant="outline-secondary"
                        onClick={handleBack}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {isViewMode && (
                  <div className="d-flex gap-3 justify-content-end mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default GroupFixturesEditPage;
